<template>
    <v-tooltip
        v-if="!isMobile"
        location="bottom"
        open-delay="200"
    >
        <template #activator="{ props: tooltipProps }">
            <span
                v-bind="tooltipProps"
                class="streak-indicator"
                data-testid="streak-indicator"
            >
                <AchievementBadge
                    type="flame"
                    :tier="BadgeTier.Gold"
                    :size="size"
                    :value="currentStreak"
                    mode="indicator"
                    label="Trenutni niz"
                />
            </span>
        </template>

        <div class="streak-indicator__tooltip">
            <div class="streak-indicator__title">Trenutni niz</div>
            <div class="streak-indicator__description">{{ lines.currentLine }}</div>
            <div class="streak-indicator__meta streak-indicator__meta--next">
                {{ lines.nextLine }}
            </div>
            <div
                v-if="lines.personalBestLine"
                class="streak-indicator__meta"
            >
                {{ lines.personalBestLine }}
            </div>
        </div>
    </v-tooltip>

    <span
        v-else
        role="button"
        tabindex="0"
        class="streak-indicator streak-indicator--clickable"
        data-testid="streak-indicator"
        aria-label="Detalji trenutnog niza"
        @click="openInfoModal"
        @keydown.enter.prevent="openInfoModal"
        @keydown.space.prevent="openInfoModal"
    >
        <AchievementBadge
            type="flame"
            :tier="BadgeTier.Gold"
            :size="size"
            :value="currentStreak"
            mode="indicator"
            label="Trenutni niz"
        />
    </span>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { BadgeTier } from '../../models/Badge';
import AchievementBadge from './AchievementBadge.vue';
import { streakLines } from './streakDisplayLines';
import { isMobileLayout, screenInfo } from '../../utils/isMobileLayout';
import { useModal } from '../../modals/useModal';
import { ModalCode } from '../../modals/ModalCode';
import { storeToRefs } from 'pinia';
import { useGenderStore } from '../../stores/genderStore';
import { type Gender } from '../../models/User';

type Props = {
    currentStreak: number;
    longestStreak: number;
    size?: number;
    /** See BadgeChip.gender. */
    gender?: Gender;
};

const props = withDefaults(defineProps<Props>(), {
    size: 48,
    gender: undefined,
});

const { showModal } = useModal();
const { gender: viewerGender } = storeToRefs(useGenderStore());

const effectiveGender = computed(() => props.gender ?? viewerGender.value);

const isMobile = computed(() => {
    // Touch screenInfo so the computed stays reactive on resize, matching
    // BadgeChip's pattern.
    void screenInfo.width;
    return isMobileLayout();
});

const lines = computed(() =>
    streakLines(props.currentStreak, props.longestStreak, effectiveGender.value),
);

function openInfoModal() {
    showModal(ModalCode.StreakInfo, {
        currentStreak: props.currentStreak,
        longestStreak: props.longestStreak,
        gender: props.gender,
    });
}
</script>

<style lang="scss" scoped>
.streak-indicator {
    display: inline-flex;
    align-items: center;
    line-height: 0;
    cursor: help;
}

.streak-indicator--clickable {
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid rgb(var(--v-theme-primary));
        outline-offset: 2px;
        border-radius: 4px;
    }
}

.streak-indicator__tooltip {
    max-width: 240px;
}

.streak-indicator__title {
    font-weight: 700;
    margin-bottom: 2px;
}

.streak-indicator__description {
    font-size: 0.85rem;
    opacity: 0.9;
}

.streak-indicator__meta {
    margin-top: 4px;
    font-size: 0.75rem;
    opacity: 0.75;
}

.streak-indicator__meta--next {
    opacity: 0.95;
    font-weight: 600;
}
</style>
